<script setup lang="ts">
/**
 * StaticItemsPanel
 *
 * Manages entity IDs for static (frozen) views.
 * Allows bulk adding and removing IDs via textarea.
 */

import { ref } from 'vue'
import { BaseButton } from '@/components'
import { savedViewService } from '../api/service'
import { useUiStore } from '@/stores/ui'

interface Props {
    viewId: string
    currentCount: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
    updated: []
}>()

const uiStore = useUiStore()
const idInput = ref('')
const isAdding = ref(false)
const isRemoving = ref(false)

/** Parse newline/comma-separated IDs */
function parseIds(): string[] {
    return idInput.value
        .split(/[\n,]+/)
        .map(id => id.trim())
        .filter(id => id.length > 0)
}

async function onAdd() {
    const ids = parseIds()
    if (ids.length === 0) return

    isAdding.value = true
    try {
        await savedViewService.addItems(props.viewId, ids)
        uiStore.success(`Added ${ids.length} item${ids.length > 1 ? 's' : ''}`)
        idInput.value = ''
        emit('updated')
    } catch {
        uiStore.error('Failed to add items')
    } finally {
        isAdding.value = false
    }
}

async function onRemove() {
    const ids = parseIds()
    if (ids.length === 0) return

    isRemoving.value = true
    try {
        await savedViewService.removeItems(props.viewId, ids)
        uiStore.success(`Removed ${ids.length} item${ids.length > 1 ? 's' : ''}`)
        idInput.value = ''
        emit('updated')
    } catch {
        uiStore.error('Failed to remove items')
    } finally {
        isRemoving.value = false
    }
}
</script>

<template>
    <div class="static-panel">
        <div class="static-panel__header">
            <span class="static-panel__label">Static View Items</span>
            <span class="static-panel__count">{{ currentCount }} items</span>
        </div>

        <div class="static-panel__body">
            <textarea
                v-model="idInput"
                class="static-panel__textarea"
                placeholder="Paste entity IDs (one per line or comma-separated)"
                rows="4"
            />
        </div>

        <div class="static-panel__actions">
            <BaseButton
                size="sm"
                :loading="isAdding"
                :disabled="!idInput.trim()"
                @click="onAdd"
            >
                Add Items
            </BaseButton>
            <BaseButton
                size="sm"
                variant="secondary"
                :loading="isRemoving"
                :disabled="!idInput.trim()"
                @click="onRemove"
            >
                Remove Items
            </BaseButton>
        </div>
    </div>
</template>

<style scoped>
.static-panel {
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-elevated);
    margin-top: 0.5rem;
}

.static-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--border-color);
}

.static-panel__label {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
}

.static-panel__count {
    font-size: var(--text-xs);
    color: var(--text-muted);
    background: var(--bg-surface);
    padding: 0.125rem 0.5rem;
    border-radius: var(--radius-full);
}

.static-panel__body {
    padding: 0.75rem;
}

.static-panel__textarea {
    width: 100%;
    padding: 0.5rem;
    font-size: var(--text-sm);
    font-family: monospace;
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    resize: vertical;
}

.static-panel__textarea:focus {
    border-color: var(--color-primary);
    outline: none;
}

.static-panel__actions {
    display: flex;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-top: 1px solid var(--border-color);
}
</style>
